import { useQuery } from "@tanstack/react-query"
import { apiGET } from "data/api/get"
import { useMetricProjectAvailability } from "./useMetricProjectAvailability"
import { useMetricsList } from "./useMetricsList"

/**
 * Returns a list of key metrics for a project.
 * e.g. https://api.tokenterminal.com/v3/internal/projects/aave/key-metrics
 * @example
 * const { data: keyMetrics } = useKeyMetricsLegacy("aave")
 */

const useKeyMetricsLegacy = (projectSlug: string) => {
  const { data: metricsList = [] } = useMetricsList()

  return useQuery({
    queryKey: ["key-metrics", projectSlug],
    queryFn: async () => {
      const result = await apiGET(
        `/v3/internal/projects/${projectSlug}/key-metrics` as "/v3/internal/projects/:slug/key-metrics"
      )

      return result.data.map((metric) => ({
        ...metric,
        label: metricsList.find((m) => m.metric_id === metric.metric_id)?.name,
      }))
    },
    staleTime: 1000 * 60 * 10,
    enabled: !!projectSlug && !!metricsList,
  })
}

export const getMarketSectorSpecificMetricIds = (marketSectorId: string) => {
  const keyMetricsProjectLookup: Record<string, string[]> = {
    base: ["fees", "bridge_deposits"],
    default: [
      "market_cap_fully_diluted",
      "token_trading_volume",
      "fees",
      "revenue",
      "user_mau",
    ],
  }

  const keyMetricsMarketSectorLookup: Record<string, string[]> = {
    "asset-management": ["capital_deployed"],
    "blockchains-l1": ["active_addresses_monthly"],
    "blockchains-l2": ["active_addresses_monthly"],
    "blockchains-l3": ["active_addresses_monthly"],
    bridges: ["transfer_volume"],
    derivatives: ["notional_trading_volume"],
    exchange: ["trading_volume"],
    gaming: ["active_users"],
    infrastructure: ["fees"],
    insurance: ["transaction_volume"],
    interoperability: ["transfer_volume"],
    lending: ["active_loans"],
    "liquid-staking": ["assets_staked"],
    nft: ["trading_volume"],
    oracle: [],
    "prediction-markets": ["trading_volume"],
    social: ["fees"],
    stablecoin: ["outstanding_supply", "stablecoin_mau"],
    "rwa-issuers": ["capital_deployed"],
    default: [
      "market_cap_fully_diluted",
      "token_trading_volume",
      "fees",
      "revenue",
      "user_mau",
    ],
  }

  const marketSectorMetricIds =
    keyMetricsMarketSectorLookup[marketSectorId] ||
    keyMetricsMarketSectorLookup.default

  const metricIds = new Set([
    ...marketSectorMetricIds,
    ...keyMetricsProjectLookup.default,
  ])

  return Array.from(metricIds)
}

/**
 * Returns a list of key non-ecosystem metric ids for a project.
 * Prioritizes market sector metrics, then project-specific key metrics, then any available metrics.
 */

export const useKeyMetricIds = ({
  projectSlug,
  dataId,
  marketSectorId,
  productId,
}: {
  projectSlug: string
  dataId: string
  marketSectorId: string
  productId?: string
}): { data: string[]; isLoading: boolean } => {
  const marketSectorMetricIds = getMarketSectorSpecificMetricIds(marketSectorId)
  const { data: keyMetrics, isLoading: isKeyMetricsLoading } =
    useKeyMetricsLegacy(projectSlug)

  const keyMetricIds = keyMetrics?.map((metric) => metric.metric_id) || []
  const {
    data: { data = [] } = { data: [] },
    isLoading: isAvailableMetricsLoading,
  } = useMetricProjectAvailability({
    dataIds: [dataId],
    productIds: productId ? [productId] : undefined,
  })

  const availableMetrics =
    data?.map((metric: { metric_id: string }) => metric.metric_id) || []

  // Prioritize market sector metrics, then fill in with key metrics and then available metrics
  const combinedMetricIds = Array.from(
    new Set([...marketSectorMetricIds, ...keyMetricIds, ...availableMetrics])
  ).filter((metricId) => availableMetrics?.includes(metricId))

  return {
    data: combinedMetricIds,
    isLoading: isKeyMetricsLoading || isAvailableMetricsLoading,
  }
}
